import React, { useState, useEffect, lazy, Suspense, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  useTheme,
  Button,
  Snackbar,
  Alert,
  AlertColor,
  CircularProgress,
  Menu,
  MenuItem,
  ThemeProvider,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import './styles/responsive.css';
import './styles/ios-style.css';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  People as PeopleIcon,
  Class as ClassIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  BarChart as BarChartIcon,
  EventNote as EventNoteIcon,
  MenuBook as MenuBookIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,

  CloudUpload as BackupIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  VpnKey as PasswordIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
  PersonOutline as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ContextSelector from './components/ContextSelector';
import DebugView from './components/DebugView';
import LoginPage from './pages/LoginPage';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import SessionExpiredDialog from './components/SessionExpiredDialog';
import ResponsiveBottomNavigation from './components/responsive/BottomNavigation';
import IOSStyleButton from './components/responsive/IOSStyleButton';
import { storageManager } from './utils/storage';
import { ReadOnlyProvider } from './contexts/ReadOnlyContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useSessionManager } from './hooks/useSessionManager';
import { dataSyncManager } from './utils/dataSync';
import { createResponsiveTheme } from './theme/responsive';
import { useResponsive } from './hooks/useResponsive';
import { performanceMonitor } from './utils/performanceMonitor';
import { pageStateManager } from './utils/pageStateManager';

// 使用MaterialUI提供的AlertColor类型作为SnackbarSeverity

// 使用懒加载替代直接导入
const StudentManagement = lazy(() => import('./components/StudentManagement'));
const ClassManagement = lazy(() => import('./components/ClassManagement'));
const SubjectManagement = lazy(() => import('./components/SubjectManagement'));
const AttendanceManagement = lazy(() => import('./components/AttendanceManagement'));
const GradeManagement = lazy(() => import('./components/GradeManagement'));
const PaymentManagement = lazy(() => import('./components/PaymentManagement'));
const TeacherManagement = lazy(() => import('./components/TeacherManagement'));
const Statistics = lazy(() => import('./components/Statistics'));
const SchoolYearManagement = lazy(() => import('./components/SchoolYearManagement'));

const BackupManager = lazy(() => import('./components/BackupManager'));

// 响应式抽屉宽度将从主题中获取

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile' })<{
  open?: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }) => {
  const drawerWidth = isMobile ? '280px' : '220px';
  
  return {
    flexGrow: 1,
    // 移动端保持原有内边距，桌面端减小内边距以实现更宽显示
    padding: isMobile ? theme.spacing(1, 1) : theme.spacing(1, 2),
    paddingBottom: isMobile ? '80px' : theme.spacing(1), // 为底部导航留空间
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: isMobile ? 0 : (open ? 0 : `-${drawerWidth}`),
    // 确保内容区域占用全部可用空间
    minHeight: 'calc(100vh - 64px)', // 减去AppBar高度
    maxWidth: '100vw', // 防止宽度溢出
    overflow: 'hidden', // 防止内容溢出
    
    ...(open && !isMobile && {
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      width: `calc(100vw - ${drawerWidth})`, // 精确计算宽度
    }),
    
    // 移动端全宽显示
    ...(isMobile && {
      marginLeft: 0,
      width: '100%',
      maxWidth: '100vw',
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    }),
    
    // 内容自适应
    '& > *': {
      maxWidth: '100%',
      overflow: 'hidden',
    },
  };
});

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})<{
  open?: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }) => {
  const drawerWidth = isMobile ? '280px' : '220px';
  
  return {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // 移动端固定宽度
    ...(isMobile && {
      width: '100%',
      marginLeft: 0,
    }),
    // 桌面端响应式宽度
    ...(!isMobile && open && {
      width: `calc(100% - ${drawerWidth})`,
      marginLeft: drawerWidth,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  };
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

// 加载指示器组件
const LoadingComponent = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    height="100%" 
    width="100%" 
    p={2}
  >
    <CircularProgress />
    <Typography variant="h6" style={{ marginLeft: 16 }}>
      加载组件中...
    </Typography>
  </Box>
);

// 当前页面渲染组件 - 移除缓存功能，直接渲染当前页面
const CurrentPageRenderer: React.FC<{
  currentPage: string;
  refreshKey: number;
}> = ({ currentPage, refreshKey }) => {
  // 监控页面切换性能
  useEffect(() => {
    performanceMonitor.startPageLoad(currentPage);
    
    return () => {
      performanceMonitor.endPageLoad(currentPage);
    };
  }, [currentPage]);

  // 根据当前页面直接渲染对应组件
  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'students':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <StudentManagement />
          </Suspense>
        );
      case 'classes':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <ClassManagement />
          </Suspense>
        );
      case 'subjects':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <SubjectManagement />
          </Suspense>
        );
      case 'attendance':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <AttendanceManagement />
          </Suspense>
        );
      case 'grades':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <GradeManagement />
          </Suspense>
        );
      case 'payments':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <PaymentManagement />
          </Suspense>
        );
      case 'teachers':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <TeacherManagement />
          </Suspense>
        );
      case 'statistics':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <Statistics />
          </Suspense>
        );
      case 'schoolYears':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <SchoolYearManagement />
          </Suspense>
        );
      case 'backup':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <BackupManager />
          </Suspense>
        );
      case 'debug':
        return <DebugView />;
      default:
        return (
          <Suspense fallback={<LoadingComponent />}>
            <SchoolYearManagement />
          </Suspense>
        );
    }
  };

  return (
    <Box key={`${currentPage}-${refreshKey}`}>
      {renderCurrentPage()}
    </Box>
  );
};

// 主应用组件，需要登录才能访问
const MainApp = () => {
  const theme = useTheme();
  const { logout, username } = useAuth();
  const navigate = useNavigate();
  const { isMobile, screenWidth } = useResponsive();
  
  const [open, setOpen] = useState(!isMobile); // 移动端默认关闭侧边栏
  const [currentPage, setCurrentPage] = useState(pageStateManager.getLastVisitedPage());
  const [refreshKey, setRefreshKey] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('info');
  const [isImportingData, setIsImportingData] = useState(false);
  const [isExportingData, setIsExportingData] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [syncMode, setSyncMode] = useState<'full' | 'incremental'>(() => {
    // 从pageStateManager恢复同步模式设置
    const savedSyncMode = pageStateManager.getSyncMode();
    console.log('[App] 恢复同步模式设置:', savedSyncMode);
    return savedSyncMode;
  });
  
  // iOS风格按钮的引用
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLDivElement>(null);

  const sessionManager = useSessionManager();

  const drawerWidth = isMobile ? '280px' : '220px';

  // 使用useMemo优化menuItems，避免重复创建
  const menuItems = useMemo(() => [
    { text: '学年管理', icon: <SchoolIcon />, value: 'schoolYears' },
    { text: '教师管理', icon: <PeopleIcon />, value: 'teachers' },
    { text: '学生管理', icon: <PeopleIcon />, value: 'students' },
    { text: '班级管理', icon: <ClassIcon />, value: 'classes' },
    { text: '科目管理', icon: <MenuBookIcon />, value: 'subjects' },
    { text: '考勤管理', icon: <EventNoteIcon />, value: 'attendance' },
    { text: '成绩管理', icon: <AssignmentIcon />, value: 'grades' },
    { text: '缴费管理', icon: <PaymentIcon />, value: 'payments' },
    { text: '统计分析', icon: <BarChartIcon />, value: 'statistics' },
    { text: '数据备份', icon: <BackupIcon />, value: 'backup' },
    { text: '系统调试', icon: <RefreshIcon />, value: 'debug' },
  ], []);

  // 优化页面切换函数，添加防抖和页面记忆
  const handlePageChange = useCallback((page: string) => {
    if (page !== currentPage) {
      // 开始性能监控
      performanceMonitor.startPageLoad(page);
      
      setCurrentPage(page);
      
      // 保存当前页面到localStorage，实现页面记忆功能
      pageStateManager.saveLastVisitedPage(page);
      
      // 移动端切换页面后自动关闭抽屉
      if (isMobile) {
        setOpen(false);
      }
      
      console.log(`🔄 切换到页面: ${page}`);
    }
  }, [currentPage, isMobile]);

  // 页面状态管理器初始化
  useEffect(() => {
    pageStateManager.initialize();
    
    const lastPage = pageStateManager.getLastVisitedPage();
    console.log(`🔄 页面记忆功能已启用，恢复到上次访问的页面: ${lastPage}`);
    
    // 如果恢复的页面不是schoolYears，说明用户之前访问了其他页面
    if (lastPage !== 'schoolYears') {
      console.log(`📖 用户上次访问的是: ${lastPage}，已自动恢复`);
    }
  }, []);

  // 注意：同步模式状态现在由pageStateManager管理，不再需要直接操作localStorage

  // 初始化令牌自动刷新机制
  useEffect(() => {
    // 启动令牌自动刷新，并传入会话过期处理函数
    import('./api/auth').then(authAPI => {
      // 设置全局会话过期处理函数
      (window as any).__sessionExpiredHandler = sessionManager.handleSessionExpired;
      authAPI.setupTokenRefresh();
      console.log('令牌自动刷新机制已初始化');
    });

    // 组件卸载时清理
    return () => {
      // 清理将由setupTokenRefresh内部处理
      (window as any).__sessionExpiredHandler = null;
    };
  }, [sessionManager.handleSessionExpired]);

  useEffect(() => {
    const { schoolYearId, termId } = storageManager.getCurrentContext();
    console.log(`[App] 初始化上下文: schoolYearId=${schoolYearId}, termId=${termId}`);
    
    if (!schoolYearId || !termId) {
      // 当没有有效上下文时，跳转到学年管理页面
      setCurrentPage('schoolYears');
      // 同时保存页面记忆，确保用户下次访问时还是从学年管理开始
      pageStateManager.saveLastVisitedPage('schoolYears');
      console.log(`📝 强制跳转到学年管理，已保存页面记忆: schoolYears`);
      setSnackbarMessage('请先设置当前学年和学期');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    
    // 添加上下文变更监听器
    const handleContextChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { schoolYearId, termId } = customEvent.detail || {};
      console.log(`[App] 上下文变更: schoolYearId=${schoolYearId}, termId=${termId}`);
      
      setRefreshKey(prev => prev + 1);
      
      // 如果成功设置了有效上下文，显示成功消息
      if (schoolYearId && termId) {
        setSnackbarMessage('已切换到新的学年和学期');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    };
    
    // 添加数据变更监听器（用于备份恢复后刷新UI）
    const handleStorageDataChanged = () => {
      console.log('[App] 检测到存储数据变更，正在刷新应用...');
      setRefreshKey(prev => prev + 1);
      setSnackbarMessage('数据已更新，应用已刷新');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    };
    
    window.addEventListener('contextChange', handleContextChange);
    window.addEventListener('storageDataChanged', handleStorageDataChanged);
    
    return () => {
      window.removeEventListener('contextChange', handleContextChange);
      window.removeEventListener('storageDataChanged', handleStorageDataChanged);
    };
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };
  
  // 用户菜单处理函数
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };
  
  // iOS风格按钮的点击处理函数
  const handleIOSUserMenuOpen = () => {
    if (userButtonRef.current) {
      setUserMenuAnchorEl(userButtonRef.current);
    }
  };
  
  const handleIOSMoreMenuOpen = () => {
    if (moreButtonRef.current) {
      setMoreMenuAnchorEl(moreButtonRef.current);
    }
  };
  
  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };
  
  const handleOpenChangePassword = () => {
    handleUserMenuClose();
    setShowChangePassword(true);
  };
  
  const handleCloseChangePassword = () => {
    setShowChangePassword(false);
  };

  const handleContextChangeFromSelector = (schoolYearId: string, termId: string) => {
    console.log(`[App] ContextSelector调用的上下文变更: schoolYearId=${schoolYearId}, termId=${termId}`);
    setRefreshKey(prev => prev + 1);
  };

  const handleSyncModeChange = (newSyncMode: 'full' | 'incremental') => {
    console.log('[App] 同步模式变更:', syncMode, '->', newSyncMode);
    setSyncMode(newSyncMode);
    pageStateManager.saveSyncMode(newSyncMode);
  };

  const handleQuickBackup = () => {
    handleMoreMenuClose();
    // 导入备份管理器并创建快速备份
    import('./utils/backup').then(({ backupManager }) => {
      backupManager.createBackup('快速备份').then(backup => {
        backupManager.exportBackupToFile(backup);
        setSnackbarMessage('快速备份已创建并导出');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }).catch(error => {
        console.error('快速备份失败:', error);
        setSnackbarMessage('快速备份失败，请查看控制台了解详情');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
    }).catch(error => {
      console.error('加载备份模块失败:', error);
      setSnackbarMessage('备份功能加载失败');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    });
  };

  // 处理数据获取
  const handleImportData = async () => {
    if (isImportingData) return;
    setIsImportingData(true);
    
    try {
      // 从D1数据库获取数据，使用选择的同步模式
      const result = syncMode === 'incremental' 
        ? await dataSyncManager.incrementalImport()
        : await dataSyncManager.importFromD1();
      
      setSnackbarMessage(result.message);
      setSnackbarSeverity(result.success ? 'success' : 'error');
      setSnackbarOpen(true);
      
      // 如果数据获取成功，自动刷新页面以显示最新数据
      if (result.success) {
        // 使用延时确保用户能看到成功消息
        setTimeout(() => {
          // 刷新所有组件
          setRefreshKey(prev => prev + 1);
          
          // 触发上下文变更事件来刷新所有管理页面的数据
          const contextChangeEvent = new Event('contextChange');
          window.dispatchEvent(contextChangeEvent);
          
          // 触发存储数据变更事件来刷新所有相关组件
          const storageChangeEvent = new Event('storageDataChanged');
          window.dispatchEvent(storageChangeEvent);
          
          // 显示额外的成功消息，告知用户数据已刷新
          setTimeout(() => {
            setSnackbarMessage(result.message + '\n\n页面数据已自动刷新！');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }, 500);
          
          // 备用选项：如果以上方法不够彻底，可以完全重新加载页面
          // window.location.reload();
        }, 1500); // 延时1.5秒让用户看到成功消息
      }
    } catch (error) {
      setSnackbarMessage(`获取数据失败: ${error instanceof Error ? error.message : '未知错误'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsImportingData(false);
    }
  };

  // 处理数据上传
  const handleExportData = async () => {
    if (isExportingData) return;
    setIsExportingData(true);
    
    try {
      // 上传到D1数据库，使用选择的同步模式
      const result = syncMode === 'incremental' 
        ? await dataSyncManager.incrementalExport()
        : await dataSyncManager.exportToD1();
      
      // 创建更详细的消息
      let detailedMessage = result.message;
      
      // 如果有失败的集合，添加更多信息
      if (result.stats.failedCollections && result.stats.failedCollections.length > 0) {
        detailedMessage += '\n\n失败的集合: ' + result.stats.failedCollections.join(', ');
        
        // 如果有错误详情，显示前3个错误
        if (result.stats.errors && result.stats.errors.length > 0) {
          const topErrors = result.stats.errors.slice(0, 3);
          detailedMessage += '\n\n错误详情 (前3个):';
          topErrors.forEach((err, idx) => {
            detailedMessage += `\n${idx + 1}. ${err.collection}: ${err.error?.substring(0, 100) || '未知错误'}${err.error?.length > 100 ? '...' : ''}`;
          });
          
          if (result.stats.errors.length > 3) {
            detailedMessage += `\n...以及其他 ${result.stats.errors.length - 3} 个错误`;
          }
          
          detailedMessage += '\n\n建议: 请尝试刷新页面后重试，或进入系统调试查看详情';
        }
      }
      
      setSnackbarMessage(detailedMessage);
      setSnackbarSeverity(result.success ? 'success' : 'warning');
      setSnackbarOpen(true);
      
      // 如果操作未完全成功，自动切换到调试视图查看详情
      if (!result.success) {
        setTimeout(() => {
          setCurrentPage('debug');
        }, 3000);
      }
    } catch (error) {
      setSnackbarMessage(`上传数据失败: ${error instanceof Error ? error.message : '未知错误'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsExportingData(false);
    }
  };



  return (
    <ReadOnlyProvider>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBarStyled 
          position="fixed" 
          open={open} 
          isMobile={isMobile}
          sx={{
            ...(screenWidth < 600 && {
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 1px 10px rgba(0, 0, 0, 0.1)',
              color: 'rgba(0, 0, 0, 0.87)'
            })
          }}
        >
          <Toolbar 
            sx={{ 
              display: 'flex', 
              // iOS风格下使用均匀分布，其他情况保持原有布局
              justifyContent: screenWidth < 600 ? 'space-evenly' : 'space-between',
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 2 },
              // 确保所有内容都在一行显示，不换行
              flexWrap: 'nowrap',
              gap: screenWidth < 600 ? 0 : { xs: 0.5, sm: 1 },
              overflow: 'hidden', // 防止内容溢出
              // iOS风格下减少内边距，给图标更多空间
              ...(screenWidth < 600 && {
                px: 0.5,
              })
            }}
          >
            {/* 左侧区域 - iOS风格下只保留必要的按钮 */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              // iOS风格下固定为auto，其他情况保持弹性
              flex: screenWidth < 600 ? '0 0 auto' : '0 1 auto',
              minWidth: 0,
              // iOS风格下限制最大宽度
              maxWidth: screenWidth < 600 ? 'auto' : { xs: '45%', sm: '50%' },
              order: screenWidth < 600 ? 2 : 1,
            }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  mr: { xs: 0.5, sm: 2 }, 
                  ...(open && { display: 'none' }),
                  // 在小屏幕上隐藏三横线菜单按钮，因为有底部导航栏
                  display: { xs: 'none', sm: 'inline-flex' }
                }}
              >
                <MenuIcon />
              </IconButton>
              
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                noWrap 
                component="div"
                sx={{ 
                  fontSize: { xs: '0.85rem', sm: '1.1rem', md: '1.25rem' },
                  fontWeight: 500,
                  mr: { xs: 0.5, sm: 2 },
                  flex: '1 1 auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  // 在iOS风格（小屏幕）下隐藏标题，节省空间
                  display: screenWidth < 600 ? 'none' : 'block'
                }}
              >
                教育机构管理系统
              </Typography>
              
              {/* 用户名显示 - 只在中等及以上屏幕显示 */}
              <Typography 
                variant="caption" 
                sx={{ 
                  ml: { xs: 0, sm: 1 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  display: { xs: 'none', md: 'block' },
                  whiteSpace: 'nowrap',
                  flex: '0 0 auto'
                }}
              >
                欢迎, {username || '用户'}
              </Typography>
            </Box>

            {/* 中间区域 - D1数据同步，iOS风格下作为主要图标区域 */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'row',
              gap: screenWidth < 600 ? 1 : 0,
              alignItems: 'center',
              // iOS风格下占据主要空间，其他情况保持原有设置
              flex: screenWidth < 600 ? '1 1 auto' : '0 0 auto',
              order: screenWidth < 600 ? 1 : 2,
              // iOS风格下居中分布所有图标，支持滚动
              justifyContent: screenWidth < 600 ? 'center' : 'center',
              overflowX: screenWidth < 600 ? 'auto' : 'visible',
              overflowY: 'hidden',
              // 隐藏滚动条但保持滚动功能
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}>
              {/* 同步模式选择 - 只在大屏幕显示 */}
              <FormControl 
                size="small" 
                sx={{ 
                  minWidth: { sm: 85, md: 100 },
                  display: { xs: 'none', lg: 'block' }
                }}
              >
                <InputLabel 
                  id="sync-mode-label" 
                  sx={{ fontSize: { md: '0.875rem' } }}
                >
                  模式
                </InputLabel>
                <Select
                  labelId="sync-mode-label"
                  id="sync-mode-select"
                  value={syncMode}
                  label="模式"
                  onChange={(e) => handleSyncModeChange(e.target.value as 'full' | 'incremental')}
                  sx={{ 
                    height: { md: 32 },
                    fontSize: { md: '0.875rem' },
                    '& .MuiSelect-select': {
                      paddingTop: { md: '6px' },
                      paddingBottom: { md: '6px' }
                    }
                  }}
                >
                  <MenuItem value="full" sx={{ fontSize: { md: '0.875rem' } }}>
                    全量同步
                  </MenuItem>
                  <MenuItem value="incremental" sx={{ fontSize: { md: '0.875rem' } }}>
                    增量同步
                  </MenuItem>
                </Select>
              </FormControl>
              
              {/* iOS风格下显示所有图标 */}
              {screenWidth < 600 && (
                <>
                  {/* 同步按钮 */}
                  <IOSStyleButton
                    icon={isImportingData ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    label="同步"
                    onClick={handleImportData}
                    disabled={isImportingData}
                    size="unified"
                    backgroundColor="#FF9500"
                    sx={{ 
                      flexShrink: 0,
                    }}
                  />
                  
                  {/* 上传按钮 */}
                  <IOSStyleButton
                    icon={isExportingData ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                    label="上传"
                    onClick={handleExportData}
                    disabled={isExportingData}
                    size="unified"
                    backgroundColor="#34C759"
                    sx={{ 
                      flexShrink: 0,
                    }}
                  />
                  
                  {/* 备份按钮 */}
                  <IOSStyleButton
                    icon={<BackupIcon />}
                    label="备份"
                    onClick={handleQuickBackup}
                    size="unified"
                    backgroundColor="#4ECDC4"
                    sx={{ 
                      flexShrink: 0,
                    }}
                  />
                  
                  {/* 设置按钮 */}
                  <Box ref={moreButtonRef} sx={{ flexShrink: 0 }}>
                    <IOSStyleButton
                      icon={<SettingsIcon />}
                      label="设置"
                      onClick={handleIOSMoreMenuOpen}
                      size="unified"
                      backgroundColor="#8E8E93"
                    />
                  </Box>
                  
                  {/* 用户按钮 */}
                  <Box ref={userButtonRef} sx={{ flexShrink: 0 }}>
                    <IOSStyleButton
                      icon={<PersonIcon />}
                      label="用户"
                      onClick={handleIOSUserMenuOpen}
                      size="unified"
                      backgroundColor="#007AFF"
                    />
                  </Box>
                </>
              )}
              
              {/* 同步按钮 - 非iOS风格 */}
              {screenWidth >= 600 && (
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={handleImportData}
                  disabled={isImportingData}
                  startIcon={
                    isImportingData ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      // 小屏幕时不显示图标，节省空间
                      { xs: 'none', sm: 'block' }.sm && <DownloadIcon sx={{ fontSize: '14px' }} />
                    )
                  }
                  sx={{ 
                    fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.875rem' },
                    px: { xs: 0.5, sm: 1, md: 1.5 },
                    py: { xs: 0.25, sm: 0.5, md: 0.75 },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    height: { xs: 28, sm: 32, md: 36 },
                    // 小屏幕时仅显示图标
                    '& .MuiButton-startIcon': {
                      display: { xs: 'none', sm: 'inline-flex' },
                      margin: 0
                    }
                  }}
                >
                  {/* 根据屏幕大小显示不同文本 */}
                  {isImportingData ? 
                    (isMobile ? '...' : '同步中') : 
                    (
                      <>
                        {/* 超小屏幕显示图标 */}
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
                          <DownloadIcon sx={{ fontSize: '14px' }} />
                        </Box>
                        {/* 小屏幕及以上显示文字 */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          {isMobile ? '同步' : '从D1同步'}
                        </Box>
                      </>
                    )
                  }
                </Button>
              )}
              
              {/* 上传按钮 - 非iOS风格 */}
              {screenWidth >= 600 && (
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleExportData}
                  disabled={isExportingData}
                  startIcon={
                    isExportingData ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      // 小屏幕时不显示图标，节省空间
                      { xs: 'none', sm: 'block' }.sm && <UploadIcon sx={{ fontSize: '14px' }} />
                    )
                  }
                  sx={{ 
                    fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.875rem' },
                    px: { xs: 0.5, sm: 1, md: 1.5 },
                    py: { xs: 0.25, sm: 0.5, md: 0.75 },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    height: { xs: 28, sm: 32, md: 36 },
                    // 小屏幕时仅显示图标
                    '& .MuiButton-startIcon': {
                      display: { xs: 'none', sm: 'inline-flex' },
                      margin: 0
                    }
                  }}
                >
                  {/* 根据屏幕大小显示不同文本 */}
                  {isExportingData ? 
                    (isMobile ? '...' : '上传中') : 
                    (
                      <>
                        {/* 超小屏幕显示图标 */}
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
                          <UploadIcon sx={{ fontSize: '14px' }} />
                        </Box>
                        {/* 小屏幕及以上显示文字 */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          {isMobile ? '上传' : '上传到D1'}
                        </Box>
                      </>
                    )
                  }
                </Button>
              )}
            </Box>
            
            {/* 右侧区域 - iOS风格下隐藏，图标移到中间区域 */}
            <Box sx={{ 
              display: screenWidth < 600 ? 'none' : 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 1, md: 1.5 },
              justifyContent: 'flex-end',
              order: 3,
              flex: '0 0 auto'
            }}>
              {/* 上下文选择器 - 只在大屏幕显示 */}
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <ContextSelector onContextChange={handleContextChangeFromSelector} />
              </Box>
              
              {/* 快速备份按钮 - 只在大屏幕显示传统按钮 */}
              <Button 
                variant="contained" 
                color="info" 
                size="small" 
                onClick={handleQuickBackup}
                startIcon={<BackupIcon sx={{ fontSize: '16px' }} />}
                sx={{ 
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', xl: 'flex' },
                  fontSize: '0.75rem',
                  px: 1,
                  py: 0.5
                }}
              >
                快速备份
              </Button>
              
              {/* 更多操作菜单 - 只在非iOS风格显示 */}
              <IconButton
                color="inherit"
                onClick={handleMoreMenuOpen}
                size={isMobile ? "small" : "medium"}
                aria-label="更多操作"
                aria-controls="more-menu"
                aria-haspopup="true"
                sx={{ 
                  display: { xs: 'flex', xl: 'none' },
                  ml: { xs: 0.5, sm: 1 }
                }}
              >
                <MoreVertIcon sx={{ fontSize: { xs: '16px', sm: '22px' } }} />
              </IconButton>
              
              {/* 用户菜单 - 只在非iOS风格显示 */}
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                size={isMobile ? "small" : "medium"}
                edge="end"
                aria-label="用户菜单"
                aria-controls="user-menu"
                aria-haspopup="true"
                sx={{ ml: { xs: 0.5, sm: 1 } }}
              >
                <AccountCircleIcon sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
              </IconButton>
              
              <Menu
                id="user-menu"
                anchorEl={userMenuAnchorEl}
                open={Boolean(userMenuAnchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleOpenChangePassword}>
                  <ListItemIcon>
                    <PasswordIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>修改密码</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>退出登录</ListItemText>
                </MenuItem>
              </Menu>
              
              {/* 更多操作菜单 */}
              <Menu
                id="more-menu"
                anchorEl={moreMenuAnchorEl}
                open={Boolean(moreMenuAnchorEl)}
                onClose={handleMoreMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiPaper-root': {
                    minWidth: 150
                  }
                }}
              >
                {/* 同步模式选择 - 在中小屏幕时显示（大屏幕时已经在顶栏显示） */}
                <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                  <MenuItem>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="mobile-sync-mode-label" sx={{ fontSize: '0.75rem' }}>
                        同步模式
                      </InputLabel>
                      <Select
                        labelId="mobile-sync-mode-label"
                        id="mobile-sync-mode-select"
                        value={syncMode}
                        label="同步模式"
                        onChange={(e) => handleSyncModeChange(e.target.value as 'full' | 'incremental')}
                        sx={{ 
                          height: 32,
                          fontSize: '0.75rem'
                        }}
                      >
                        <MenuItem value="full" sx={{ fontSize: '0.75rem' }}>
                          全量同步
                        </MenuItem>
                        <MenuItem value="incremental" sx={{ fontSize: '0.75rem' }}>
                          增量同步
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </MenuItem>
                  <Divider />
                </Box>
                

                
                {/* 上下文选择器 - 在中小屏幕时显示（大屏幕时已经在顶栏显示） */}
                <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                  <Divider />
                  <MenuItem>
                    <Box sx={{ width: '100%', p: 1 }}>
                      <ContextSelector onContextChange={handleContextChangeFromSelector} />
                    </Box>
                  </MenuItem>
                </Box>
              </Menu>
            </Box>
          </Toolbar>
        </AppBarStyled>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' }, // 在小屏幕上隐藏侧边栏
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant={isMobile ? "temporary" : "persistent"}
          anchor="left"
          open={open}
          onClose={isMobile ? handleDrawerClose : undefined}
          ModalProps={{
            keepMounted: true, // 移动端性能优化
          }}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {menuItems.map((item) => (
              <ListItem
                button
                key={item.value}
                onClick={() => handlePageChange(item.value)}
                selected={currentPage === item.value}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Drawer>
        <Main open={open} isMobile={isMobile}>
          {!isMobile && <DrawerHeader />}
          <Box sx={{ 
            minHeight: 'calc(100vh - 64px)', // 确保内容区域足够高
            paddingBottom: isMobile ? '80px' : '16px', // 为移动端底部导航留出空间
          }}>
            <CurrentPageRenderer currentPage={currentPage} refreshKey={refreshKey} />
          </Box>
        </Main>
        
        {/* 移动端底部导航 */}
        <ResponsiveBottomNavigation 
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
        
        {/* 全局消息提示 */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarSeverity === 'success' ? 6000 : 15000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ 
            maxWidth: '80%', 
            '& .MuiPaper-root': { maxWidth: '100%' } 
          }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ 
              width: '100%', 
              whiteSpace: 'pre-line',  // 允许显示换行符
              '& .MuiAlert-message': {
                maxHeight: '300px',
                overflow: 'auto'
              }
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        
        {/* 修改密码对话框 */}
        <ChangePasswordDialog 
          open={showChangePassword} 
          onClose={handleCloseChangePassword} 
        />
        
        {/* 会话过期对话框 */}
        <SessionExpiredDialog
          open={sessionManager.showSessionDialog}
          onRelogin={sessionManager.handleRelogin}
          onRetry={sessionManager.sessionError?.isNetworkError ? sessionManager.handleRetry : undefined}
          message={sessionManager.sessionError?.message}
          isExpired={sessionManager.sessionError?.isExpired || false}
          isNetworkError={sessionManager.sessionError?.isNetworkError || false}
        />
      </Box>
    </ReadOnlyProvider>
  );
};

// 路由保护组件，确保只有登录用户才能访问受保护的路由
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// 登录页面包装器，用于正确使用useAuth钩子
const LoginPageWrapper = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  
  // 如果已经登录，重定向到主页
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/');
    }
  }, [auth.isAuthenticated, navigate]);
  
  const handleLogin = async (username: string, password: string) => {
    try {
      const result = await auth.login(username, password);
      
      if (result.success) {
        // 登录成功后导航到主页
        navigate('/');
        return true;
      }
      return false;
    } catch (error) {
      console.error("登录过程中发生错误:", error);
      // 将错误向上传递给LoginPage组件处理
      throw error;
    }
  };
  
  return <LoginPage onLogin={handleLogin} />;
};

// 主App组件，包含路由和认证提供者
export default function App() {
  const responsiveTheme = createResponsiveTheme('light');
  
  return (
    <ThemeProvider theme={responsiveTheme}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPageWrapper />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}
